import { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/axios';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(
    () => localStorage.getItem('vflow_business') 
      ? JSON.parse(localStorage.getItem('vflow_business')) 
      : null
  );
  const [availableBusinesses, setAvailableBusinesses] = useState(
    () => localStorage.getItem('vflow_available_businesses')
      ? JSON.parse(localStorage.getItem('vflow_available_businesses'))
      : []
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('vflow_token');
    const savedUser = localStorage.getItem('vflow_user');
    if (token && savedUser) {
      api.get('/auth/me')
        .then(() => {
          setUser(JSON.parse(savedUser));
          return api.get('/businesses/my', {
            headers: { Authorization: 'Bearer ' + token }
          });
        })
        .then((bizRes) => {
          const biz = bizRes.data;
          // Don't clobber a business the user already picked from the
          // switcher (stored in localStorage) with the server's default
          // "first business" - only fall back to it if nothing is saved.
          const savedBizRaw = localStorage.getItem('vflow_business');
          const savedBiz = savedBizRaw ? JSON.parse(savedBizRaw) : null;
          if (savedBiz && savedBiz.id) {
            setSelectedBusiness(savedBiz);
          } else if (biz && biz.id) {
            localStorage.setItem('vflow_business', JSON.stringify(biz));
            setSelectedBusiness(biz);
          } else {
            localStorage.removeItem('vflow_business');
            setSelectedBusiness(null);
          }
          return api.get('/businesses/siblings', {
            headers: { Authorization: 'Bearer ' + token }
          });
        })
        .then((sibRes) => {
          const sibs = sibRes.data;
          if (sibs && sibs.length) {
            localStorage.setItem('vflow_available_businesses', JSON.stringify(sibs));
            setAvailableBusinesses(sibs);
          }
        })
        .catch(() => {
          localStorage.removeItem('vflow_token');
          localStorage.removeItem('vflow_user');
          localStorage.removeItem('vflow_business');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('vflow_token', data.token);
    localStorage.setItem('vflow_user', JSON.stringify(data.user));
    // Some users (e.g. one login shared across sibling businesses under the
    // same Meta Business Manager) get more than one selectable business.
    if (data.available_businesses && data.available_businesses.length) {
      localStorage.setItem('vflow_available_businesses', JSON.stringify(data.available_businesses));
      setAvailableBusinesses(data.available_businesses);
    } else {
      localStorage.removeItem('vflow_available_businesses');
      setAvailableBusinesses([]);
    }
    // Login aana udane correct business auto-set pannrom
    try {
      const bizRes = await api.get('/businesses/my', {
        headers: { Authorization: 'Bearer ' + data.token }
      });
      const biz = bizRes.data;
      if (biz && biz.id) {
        localStorage.setItem('vflow_business', JSON.stringify(biz));
        setSelectedBusiness(biz);
      } else {
        localStorage.removeItem('vflow_business');
        setSelectedBusiness(null);
      }
    } catch (e) {
      localStorage.removeItem('vflow_business');
      setSelectedBusiness(null);
    }
    setUser(data.user);
    return data;
  };

  const loginWithToken = async (token, userData) => {
    localStorage.setItem('vflow_token', token);
    localStorage.setItem('vflow_user', JSON.stringify(userData));
    try {
      const bizRes = await api.get('/businesses/my', {
        headers: { Authorization: 'Bearer ' + token }
      });
      const biz = bizRes.data;
      if (biz && biz.id) {
        localStorage.setItem('vflow_business', JSON.stringify(biz));
        setSelectedBusiness(biz);
      } else {
        localStorage.removeItem('vflow_business');
        setSelectedBusiness(null);
      }
    } catch (e) {
      localStorage.removeItem('vflow_business');
      setSelectedBusiness(null);
    }
    setUser(userData);
  };
  const logout = () => {
    localStorage.clear();
    setUser(null);
  };

  const selectBusiness = (biz) => {
    setSelectedBusiness(biz);
    localStorage.setItem('vflow_business', JSON.stringify(biz));
  };

  return (
    <AuthContext.Provider value={{ user, selectedBusiness, selectBusiness, availableBusinesses, login, loginWithToken, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
